import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
} from 'react';
import { useActions, useLive, useNow, useRuntime } from './context';
import { formatClock, formatMoney, isoDayIn } from '../mirror/time';
import { BookSheet } from './BookSheet';
import type { BookSheetPrefill } from './BookSheet';
import { useSeat } from './seat';
import { sourceWord } from './Today';
import { useLocale, useT } from '../messages';
import type { T, UiLocale } from '../messages';
import { StaffRequest } from '../wire';
import type { Blackout, Resource, Service } from '../wire';
import type { DayHours } from '@yourstore/domain';
import type { MirroredAppointment } from '../db/schema';
import {
  addIsoDays,
  calendarPolicy,
  clashFor,
  clockLabel,
  closureBands,
  daysFrom,
  gapAfter,
  gridBounds,
  hasLanes,
  hourMarks,
  isDrawable,
  laneResources,
  lengthStops,
  moveTarget,
  nearestStop,
  occupiesLane,
  openBands,
  packColumns,
  placeBlock,
  queuedMoves,
  snapToGrid,
  stepStop,
  wallClockOf,
  wallClockToIso,
  weekStartOf,
  weekdayIndex,
  withQueuedMove,
} from './calendar-grid';
import type {
  CalendarPolicy,
  CandidateMove,
  GridBounds,
  LengthStop,
  PlacedBlock,
} from './calendar-grid';

/**
 * The calendar — a time grid, a day or a week of it, in the tenant's zone.
 *
 * It replaces the half-hour table this screen used to be, and the reason is the
 * owner's own (`docs/lovespa-discovery.md` §1, §2.4): she packs her day *"like a
 * jigsaw"*, from a paper book open on the desk, and she wants it on her phone
 * **and** on a screen at the desk. A table of equal half-hour cells cannot show
 * a jigsaw — a fifteen-minute brow wax and a ninety-minute facial were the same
 * size on it, and the half hour she keeps between guests (§2.2, *"I do a half an
 * hour between clients"*) was not drawn at all, so the one thing she needs to
 * see when squeezing somebody in — where the real room is — was the one thing
 * missing.
 *
 * ## What the grid claims, and what it does not
 *
 * The line `CalendarView` has always drawn is unchanged and is the reason this
 * screen can be trusted offline: **the grid is the mirrored book, not
 * availability.** An empty stretch means *"nothing is booked in the copy of the
 * book this device holds"*. It does **not** mean "this is free" — only
 * `GET /v1/availability` can say that, because only `packages/scheduling` knows
 * about holds, blackouts, lead times, pacing and capacity, and there is exactly
 * one implementation of it (dev.md rule 3).
 *
 * So: tapping an empty stretch opens the booking form pre-filled with that time
 * rather than asserting it is free; dropping a dragged block shows what this
 * device can see in the way as a **warning** and lets the drop through anyway;
 * and the server settles both, with the enriched `409` becoming an owner task
 * (§8.3). The calendar being *usefully* wrong offline and *honestly* wrong is
 * the whole trade of §8.
 *
 * ## The two numbers it borrows
 *
 * The house gap after each booking is resolved by `buffersFor` in
 * `@yourstore/scheduling` — imported, never restated (A2) — out of the item's
 * own buffers and the tenant's defaults, which ride `GET /v1/session` and are
 * mirrored with it. The step a drag snaps to and a keyboard arrow moves by is
 * the tenant's `slot_grid_min`, from the same place, so a time reached by
 * dragging is a time the engine would have offered.
 *
 * ## Lanes
 *
 * One column per bookable resource in Day view, and a filter in Week view —
 * **but only when there is more than one**. A solo esthetician sees no lane
 * chrome at all, because a header reading "Room One" above the only room is a
 * word she has to read every morning to learn nothing. The lanes are read from
 * the mirror's `resources`, so whatever the Team screen adds appears here with
 * no edit to this file.
 */
export function CalendarView(): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();
  const t = useT();
  const locale = useLocale();
  /**
   * **Seeded from the synchronous snapshot, not from `null`.**
   *
   * Every instant on this screen is composed out of a day, a minute and the
   * tenant's zone — so a first paint that believed the zone was UTC would let a
   * tap in the first few milliseconds book four hours off, silently, with the
   * grid redrawn correctly a tick later. `sessionNow()` exists for exactly this
   * (see `Mirror`'s note on it); the effect still corrects a cold open.
   */
  const { session, canWrite } = useSeat();
  const timezone = session?.tenant.timezone ?? 'UTC';
  const currency = session?.tenant.currency ?? 'USD';
  const policy = useMemo(() => calendarPolicy(session), [session]);
  /*
   * **A seat that cannot write reads the grid and moves nothing** (L15,
   * `seat.tsx`). Every gesture on this screen is an intent — a drag, an arrow
   * key, a resize, a tap on an empty stretch — and `core-api` refuses each of
   * them for a `readonly` seat and for the console's support look. So the
   * three entry points below return before a `DragState` exists, the column
   * draws no booking surface and no resize handle, and a block is a labelled
   * note rather than a button: the keyboard path is closed the same way the
   * pointer path is, because a calendar that refused a drag and honoured an
   * arrow would be a calendar with a second rule.
   */

  // Derived until the owner picks a day, rather than seeded once in `useState`:
  // the session (and with it the tenant's timezone) arrives from IndexedDB a
  // tick after the first render, and a calendar seeded in UTC would open on
  // tomorrow for the last four hours of every day in the Caribbean.
  const [picked, setPicked] = useState<string | null>(null);
  const today = isoDayIn(timezone, runtime.now());
  const day = picked ?? today;

  const [mode, setMode] = useState<'day' | 'week'>('week');
  const [lane, setLane] = useState<string>('');
  const [desk, setDesk] = useState(false);
  const [booking, setBooking] = useState<BookSheetPrefill | null>(null);
  const narrow = useNarrowViewport();

  const resources = useLive(() => runtime.mirror.resources(), []);
  const services = useLive(() => runtime.mirror.services(), []);
  const hours = useLive(() => runtime.mirror.hours(), null);
  const blackouts = useLive(() => runtime.mirror.blackouts(), []);
  const pending = useLive(() => runtime.outbox.pending(), []);

  /**
   * How many days are on screen, and from which.
   *
   * **Week collapses to three days on a phone, never to a horizontal scroll.**
   * She asked for this on her phone and at the desk, and the two are different
   * shapes rather than the same shape at two sizes: seven columns at 375px are
   * 45 pixels wide, which is narrower than the name that has to fit in them.
   * Three is the smallest span that still shows *shape* — today, and what is
   * either side of it — which is what a jigsaw needs and what an agenda list
   * throws away.
   */
  const span = mode === 'day' ? 1 : narrow ? 3 : 7;
  const from = mode === 'day' ? day : narrow ? day : weekStartOf(day);
  const days = useMemo(() => daysFrom(from, span), [from, span]);
  const dayKey = days.join(',');

  const mirrored = useLive(
    async () => (await Promise.all(days.map((d) => runtime.mirror.day(d)))).flat(),
    [],
    [dayKey],
  );

  /* ---------------------------------------------------------------- */
  /* The book this screen draws                                        */
  /* ---------------------------------------------------------------- */

  /**
   * The mirror, with every queued move laid over it.
   *
   * A reschedule has no optimistic row of its own — an edit shows through the
   * row it edits — so without this the block the owner just dragged would snap
   * back on the next repaint and stay back until the server answered, which
   * offline is never. See `queuedMoves` for why the 409 snap-back then costs
   * nothing: a rejected intent is no longer pending, the overlay lifts, and the
   * block is drawn where the cloud says it is.
   */
  const moves = useMemo(() => queuedMoves(pending), [pending]);
  const rows = useMemo(
    () =>
      mirrored.filter(isDrawable).map((row) => {
        const move = moves.get(row.appointment_id);
        return move === undefined ? row : withQueuedMove(row, move);
      }),
    [mirrored, moves],
  );

  const lanes = useMemo(() => laneResources(resources), [resources]);
  const laneChrome = hasLanes(resources);
  // A row belongs to a lane it occupies as primary *or* as companion (0056):
  // the massage that needs Maria and Room 1 is on Maria's book and on the
  // room's, and a filter on either has to find it.
  const filtered = useMemo(
    () => (lane === '' ? rows : rows.filter((row) => occupiesLane(row, lane))),
    [rows, lane],
  );

  /**
   * The columns, as data.
   *
   * Day view with more than one bookable resource draws one column per
   * resource; everything else draws one column per day. That is the whole
   * branch, and it lives here rather than in the JSX so that `Column` below
   * knows nothing about which mode produced it.
   */
  const columns: ColumnSpec[] = useMemo(() => {
    if (mode === 'day' && laneChrome && lane === '') {
      return lanes.map((resource) => ({
        key: resource.resource_id,
        day,
        resource,
        shades: resource,
        heading: resource.name,
      }));
    }
    const only = lane === '' ? null : (lanes.find((r) => r.resource_id === lane) ?? null);
    // A single bookable resource is unambiguous, so an unlabelled column still
    // knows whose hours and whose time off to shade — see `ColumnSpec.shades`.
    const shades = only ?? (lanes.length === 1 ? (lanes[0] ?? null) : null);
    return days.map((d) => ({ key: d, day: d, resource: only, shades, heading: null }));
  }, [mode, laneChrome, lane, lanes, days, day]);

  /* ---------------------------------------------------------------- */
  /* Geometry                                                          */
  /* ---------------------------------------------------------------- */

  const placedByColumn = useMemo(() => {
    const out = new Map<string, PlacedBlock[]>();
    for (const column of columns) {
      const inColumn = filtered.filter((row) => {
        if (wallClockOf(row.slot.start, timezone).day !== column.day) return false;
        if (column.resource !== null && !occupiesLane(row, column.resource.resource_id)) {
          return false;
        }
        return true;
      });
      out.set(
        column.key,
        packColumns(
          inColumn.map((row) =>
            placeBlock(
              row,
              timezone,
              gapAfter(row, services, policy),
              moves.has(row.appointment_id),
            ),
          ),
        ),
      );
    }
    return out;
  }, [columns, filtered, timezone, services, policy, moves]);

  const bounds = useMemo(() => {
    const spans = [...placedByColumn.values()]
      .flat()
      .map((block) => ({ topMin: block.topMin, endMin: block.endMin + block.tailMin }));
    const windows = columns.flatMap((column) =>
      (hoursForDay(column.resource, hours?.hours ?? [], column.day)?.windows ?? []).map((w) => ({
        start: w.start,
        end: w.end,
      })),
    );
    return gridBounds(windows, spans);
  }, [placedByColumn, columns, hours]);

  /* ---------------------------------------------------------------- */
  /* Dragging                                                          */
  /* ---------------------------------------------------------------- */

  const columnRefs = useRef(new Map<string, HTMLDivElement>());
  const [drag, setDrag] = useState<DragState | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const dragRef = useRef<DragState | null>(null);
  dragRef.current = drag;

  /**
   * What the gesture in progress is asking for.
   *
   * Both shapes are a `CandidateMove` on purpose: the clash warning, the ghost
   * and the confirm sheet all want "a room, a start and a length", and a
   * resize is that with the start held still. It keeps one overlap check for
   * two gestures rather than two that could disagree about the gap.
   *
   * **Every lane, on every path** (L11): a move — pointer or keyboard, both
   * arrive here as the same `DragState` — goes through `moveTarget`, which
   * carries the block's companion lanes (0056) minus the one being dropped
   * into; a resize names them itself. So the ghost's warning reads the room
   * as well as the person whichever way the block was picked up, and
   * `calendar.test.tsx` ("warns from the keyboard when the room — not the
   * person — is taken") is the proof for the path jsdom can drive.
   */
  const candidateOf = useCallback(
    (state: DragState): CandidateMove =>
      state.mode === 'length'
        ? {
            appointmentId: state.block.appointment.appointment_id,
            resourceId: state.block.appointment.slot.resource_id,
            alsoResourceIds: state.block.appointment.slot.also_resource_ids,
            startIso: state.block.appointment.slot.start,
            durationMin: state.minutes,
            serviceId: state.block.appointment.service_id,
          }
        : moveTarget(state.block, state.day, state.minutes, state.resourceId, timezone, policy),
    [timezone, policy],
  );

  const clash = useMemo(() => {
    if (drag === null) return undefined;
    return clashFor(candidateOf(drag), rows, services, policy);
  }, [drag, candidateOf, rows, services, policy]);

  /** Where in the grid a pointer is, as a column and a minute. */
  const locate = useCallback(
    (clientX: number, clientY: number): { column: ColumnSpec; minutes: number } | null => {
      for (const column of columns) {
        const element = columnRefs.current.get(column.key);
        if (element === undefined) continue;
        const rect = element.getBoundingClientRect();
        // A zero-width rect is jsdom, or a column that has not laid out yet;
        // either way it cannot contain a pointer and must not swallow one.
        if (rect.width === 0 && rect.height === 0) continue;
        if (clientX < rect.left || clientX >= rect.right) continue;
        const ratio = (clientY - rect.top) / rect.height;
        return {
          column,
          minutes: bounds.fromMin + ratio * (bounds.toMin - bounds.fromMin),
        };
      }
      return null;
    },
    [columns, bounds],
  );

  useEffect(() => {
    if (drag === null || drag.via !== 'pointer') return;
    const move = (event: globalThis.PointerEvent): void => {
      const state = dragRef.current;
      if (state === null || state.pointerId !== event.pointerId) return;
      event.preventDefault();
      const at = locate(event.clientX, event.clientY);
      if (at === null) return;
      if (state.mode === 'length') {
        // The bottom edge follows the pointer; the top stays exactly where the
        // guest was told it would be. On a service sold at several lengths it
        // snaps to *her* lengths rather than the grid, and remembers which
        // option it landed on — that is what the intent will name.
        const stop = nearestStop(
          at.minutes - state.block.topMin,
          lengthStops(state.block.appointment, services),
        );
        setDrag({
          ...state,
          minutes: stop?.minutes ?? clampLength(at.minutes - state.block.topMin, policy),
          stop,
          armed: true,
        });
        return;
      }
      setDrag({
        ...state,
        day: at.column.day,
        minutes: snapToGrid(at.minutes - state.grabOffsetMin, policy.slotGridMin),
        resourceId: at.column.resource?.resource_id ?? state.resourceId,
        armed: true,
      });
    };
    const up = (event: globalThis.PointerEvent): void => {
      const state = dragRef.current;
      if (state === null || state.pointerId !== event.pointerId) return;
      setDrag(null);
      if (state.armed) setConfirm(confirmFor(state, candidateOf(state)));
    };
    const cancel = (): void => setDrag(null);
    globalThis.addEventListener?.('pointermove', move, { passive: false });
    globalThis.addEventListener?.('pointerup', up);
    globalThis.addEventListener?.('pointercancel', cancel);
    return () => {
      globalThis.removeEventListener?.('pointermove', move);
      globalThis.removeEventListener?.('pointerup', up);
      globalThis.removeEventListener?.('pointercancel', cancel);
    };
  }, [drag, locate, policy, candidateOf, services]);

  /**
   * **Touch picks a block up on a long press, so the page still scrolls.**
   *
   * A grid that started dragging on the first touch move would be a grid a
   * thumb cannot scroll, which on the device she actually carries is worse than
   * having no drag at all. 350 ms is the platform's own threshold for "this is
   * a hold, not a swipe"; a move of more than eight pixels before it fires is a
   * scroll and cancels the pick-up.
   */
  const beginPointerDrag = (
    event: ReactPointerEvent<HTMLElement>,
    block: PlacedBlock,
    column: ColumnSpec,
    dragMode: DragMode = 'move',
  ): void => {
    if (!canWrite) return;
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    // The bottom edge is its own gesture and must not also pick the block up.
    event.stopPropagation();
    const element = columnRefs.current.get(column.key);
    const rect = element?.getBoundingClientRect();
    const total = bounds.toMin - bounds.fromMin;
    const pointerMin =
      rect === undefined || rect.height === 0
        ? block.topMin
        : bounds.fromMin + ((event.clientY - rect.top) / rect.height) * total;

    const state: DragState = {
      block,
      pointerId: event.pointerId,
      grabOffsetMin: Math.max(0, pointerMin - block.topMin),
      day: column.day,
      minutes: dragMode === 'length' ? block.heightMin : block.topMin,
      resourceId: block.appointment.slot.resource_id,
      via: 'pointer',
      mode: dragMode,
      // A mouse is armed at once; a finger has to hold.
      armed: event.pointerType !== 'touch',
      startX: event.clientX,
      startY: event.clientY,
    };

    if (event.pointerType === 'touch') {
      const timer = globalThis.setTimeout(() => setDrag({ ...state, armed: true }), 350);
      const abort = (e: globalThis.PointerEvent): void => {
        if (Math.abs(e.clientX - state.startX) + Math.abs(e.clientY - state.startY) > 8) {
          globalThis.clearTimeout(timer);
          globalThis.removeEventListener?.('pointermove', abort);
        }
      };
      globalThis.addEventListener?.('pointermove', abort, { passive: true });
      globalThis.setTimeout(() => globalThis.removeEventListener?.('pointermove', abort), 400);
      return;
    }
    setDrag(state);
  };

  /**
   * **The keyboard path, which is not optional.**
   *
   * Focus a block, arrows move it — up and down by the house's own
   * `slot_grid_min`, left and right by a day (or a lane, in Day view) — Enter
   * confirms and Escape cancels. It is the same intent, the same warning and the
   * same confirm sheet as a drag, because it is the same operation; what differs
   * is only how the new time was named. A calendar whose only way to move an
   * appointment is a gesture is a calendar a keyboard cannot run and a screen
   * reader cannot describe.
   */
  const onBlockKey = (
    event: ReactKeyboardEvent<HTMLElement>,
    block: PlacedBlock,
    column: ColumnSpec,
    dragMode: DragMode = 'move',
  ): void => {
    if (!canWrite) return;
    const state: DragState = drag ?? {
      block,
      pointerId: -1,
      grabOffsetMin: 0,
      day: column.day,
      minutes: dragMode === 'length' ? block.heightMin : block.topMin,
      resourceId: block.appointment.slot.resource_id,
      via: 'keyboard',
      mode: dragMode,
      armed: true,
      startX: 0,
      startY: 0,
    };
    const step = policy.slotGridMin;
    const lengthMode = state.mode === 'length';
    /*
     * **Her lengths, one per key press**, on a service sold at several: the
     * 120 is one ArrowUp from the 90, not two quarter hours. A service sold
     * one way keeps the house grid's step.
     */
    const stops = lengthMode ? lengthStops(state.block.appointment, services) : [];
    const lengthBy = (direction: 1 | -1): Pick<DragState, 'minutes' | 'stop'> => {
      const stop = stepStop(state.minutes, stops, direction);
      return stop === undefined
        ? { minutes: clampLength(state.minutes + direction * step, policy), stop: undefined }
        : { minutes: stop.minutes, stop };
    };

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        setDrag({
          ...state,
          ...(lengthMode ? lengthBy(-1) : { minutes: Math.max(0, state.minutes - step) }),
        });
        return;
      case 'ArrowDown':
        event.preventDefault();
        setDrag({
          ...state,
          ...(lengthMode ? lengthBy(1) : { minutes: Math.min(1440 - 5, state.minutes + step) }),
        });
        return;
      case 'ArrowLeft':
      case 'ArrowRight': {
        event.preventDefault();
        // A length has no sideways. Left and right belong to the block's body.
        if (lengthMode) return;
        const delta = event.key === 'ArrowLeft' ? -1 : 1;
        if (mode === 'day' && laneChrome && lane === '') {
          const index = lanes.findIndex((r) => r.resource_id === state.resourceId);
          const next = lanes[Math.min(lanes.length - 1, Math.max(0, index + delta))];
          if (next !== undefined) setDrag({ ...state, resourceId: next.resource_id });
          return;
        }
        setDrag({ ...state, day: addIsoDays(state.day, delta) });
        return;
      }
      case 'Enter':
        if (drag === null) return;
        event.preventDefault();
        setDrag(null);
        setConfirm(confirmFor(state, candidateOf(state)));
        return;
      case 'Escape':
        if (drag === null) return;
        event.preventDefault();
        setDrag(null);
        return;
      default:
    }
  };

  /* ---------------------------------------------------------------- */
  /* Render                                                            */
  /* ---------------------------------------------------------------- */

  const marks = hourMarks(bounds);
  const total = bounds.toMin - bounds.fromMin;
  const nowMinutes = wallClockOf(runtime.now(), timezone).minutes;
  useNow(60_000);

  const grid = (
    <div
      className={`cal${narrow ? ' cal--narrow' : ''}`}
      style={{ ['--cal-span' as string]: String(total) } as CSSProperties}
    >
      <div className="cal__ruler" aria-hidden="true">
        {marks.map((minute) => (
          <span
            key={minute}
            className="cal__mark"
            style={{ top: `calc(var(--cal-ppm) * ${minute - bounds.fromMin}px)` }}
          >
            {clockLabel(minute)}
          </span>
        ))}
      </div>

      <div className="cal__columns">
        {columns.map((column) => (
          <Column
            key={column.key}
            column={column}
            columns={columns.length}
            bounds={bounds}
            marks={marks}
            blocks={placedByColumn.get(column.key) ?? []}
            hours={hours?.hours ?? []}
            blackouts={blackouts}
            timezone={timezone}
            currency={currency}
            locale={locale}
            t={t}
            today={today}
            nowMinutes={nowMinutes}
            drag={drag}
            clash={clash}
            canWrite={canWrite}
            showHeading={mode !== 'day' || column.heading !== null}
            register={(element) => {
              if (element === null) columnRefs.current.delete(column.key);
              else columnRefs.current.set(column.key, element);
            }}
            onPickUp={beginPointerDrag}
            onKey={onBlockKey}
            onBook={(minutes) =>
              setBooking({
                startIso: isoAt(column.day, minutes, timezone),
                ...(column.resource === null ? {} : { resourceId: column.resource.resource_id }),
                source: 'staff',
              })
            }
          />
        ))}
      </div>
    </div>
  );

  return (
    <section className={`view${desk ? ' view--desk' : ''}`}>
      <div className="view__header cal__header">
        <div className="date-picker">
          <button
            type="button"
            className="button"
            aria-label={t('calendar.previous')}
            onClick={() => setPicked(addIsoDays(from, -span))}
          >
            ‹
          </button>
          <input
            type="date"
            aria-label={t('calendar.pickDay')}
            value={day}
            onChange={(e) => setPicked(e.target.value === '' ? null : e.target.value)}
          />
          <button
            type="button"
            className="button"
            aria-label={t('calendar.next')}
            onClick={() => setPicked(addIsoDays(from, span))}
          >
            ›
          </button>
          <button type="button" className="button" onClick={() => setPicked(today)}>
            {t('calendar.today')}
          </button>
        </div>

        <h1>{headingFor(days, timezone, locale)}</h1>

        <div className="row-actions">
          <div className="cal__modes" role="group" aria-label={t('calendar.viewLabel')}>
            <button
              type="button"
              className={`button${mode === 'day' ? ' is-current' : ''}`}
              aria-pressed={mode === 'day'}
              onClick={() => setMode('day')}
            >
              {t('calendar.dayView')}
            </button>
            <button
              type="button"
              className={`button${mode === 'week' ? ' is-current' : ''}`}
              aria-pressed={mode === 'week'}
              onClick={() => setMode('week')}
            >
              {t('calendar.weekView')}
            </button>
          </div>

          {/* Lane chrome exists only where lanes do — a solo owner sees none. */}
          {laneChrome ? (
            <label className="cal__filter">
              <span className="visually-hidden">{t('calendar.whichResource')}</span>
              <select value={lane} onChange={(e) => setLane(e.target.value)}>
                <option value="">{t('calendar.allResources')}</option>
                {lanes.map((resource) => (
                  <option key={resource.resource_id} value={resource.resource_id}>
                    {resource.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <button type="button" className="button" onClick={() => setDesk(!desk)}>
            {desk ? t('calendar.exitDesk') : t('calendar.deskMode')}
          </button>
        </div>
      </div>

      {/*
       * **The keyboard's instructions, said once and where they are needed.**
       * `aria-describedby` on every block points at this, so a screen reader
       * reads "move with the arrow keys" when a block takes focus rather than
       * leaving the only non-gesture path undiscoverable.
       */}
      {canWrite ? (
        <p className="cal__hint" id="cal-move-hint">
          {t('calendar.moveHint', { minutes: policy.slotGridMin })}
        </p>
      ) : null}

      {grid}

      {/*
       * The two things a grid cannot say by being empty. A day the tenant is
       * shut is shaded, which reads as "nothing here" from across a room — so
       * Day view says the word as well. A tenant with no hours document at all
       * would otherwise get a plausible-looking eight-to-seven grid that is
       * entirely invented, and the honest thing is to name the gap and where it
       * is filled in.
       */}
      {mode === 'day' &&
      hoursForDay(columns[0]?.resource ?? null, hours?.hours ?? [], day)?.closed === true ? (
        <p className="empty">{t('calendar.closedToday')}</p>
      ) : null}
      {(hours?.hours ?? []).length === 0 ? <p className="empty">{t('calendar.noHours')}</p> : null}

      {booking !== null ? (
        <BookSheet
          prefill={booking}
          onDone={() => setBooking(null)}
          onCancel={() => setBooking(null)}
        />
      ) : null}

      {confirm !== null ? (
        <MoveSheet
          confirm={confirm}
          timezone={timezone}
          currency={currency}
          rows={rows}
          services={services}
          policy={policy}
          onCancel={() => setConfirm(null)}
          onMove={async () => {
            const { block, candidate } = confirm;
            await actions.reschedule({
              appointmentId: block.appointment.appointment_id,
              startIso: candidate.startIso,
              ...(candidate.resourceId === block.appointment.slot.resource_id
                ? {}
                : { resourceId: candidate.resourceId }),
              clientName: block.appointment.client.display_name,
              serviceName: block.appointment.service_name,
            });
            setConfirm(null);
          }}
          onPropose={async () => {
            await proposeAnotherTime(runtime, confirm);
            setConfirm(null);
          }}
          onLength={async () => {
            const { block, candidate } = confirm;
            await actions.setAppointmentLength({
              appointmentId: block.appointment.appointment_id,
              durationMin: candidate.durationMin,
              // The option the edge snapped to — an id, never its price (B7).
              ...(confirm.stop === undefined ? {} : { optionId: confirm.stop.optionId }),
              clientName: block.appointment.client.display_name,
              serviceName: block.appointment.service_name,
              whenIso: block.appointment.slot.start,
            });
            setConfirm(null);
          }}
        />
      ) : null}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* One column                                                          */
/* ------------------------------------------------------------------ */

interface ColumnSpec {
  key: string;
  day: string;
  /** The lane this column is, or `null` for "every resource, one column". */
  resource: Resource | null;
  /**
   * **Whose hours and whose time off this column shades** — which is not always
   * the same question as "which lane is this".
   *
   * A solo owner has one bookable resource and therefore no lane chrome at all
   * (`hasLanes`), so `resource` is `null` and, before this field existed, her
   * own weekly hours and her own days off were drawn **nowhere**: the Team
   * screen always stamps a `resource_id` on time off, and the one column was
   * asking for the house's. That is the tenant this calendar was written for.
   *
   * Deliberately a *second* field rather than filling `resource` in: that one
   * also decides which blocks land in the column, and a retired person's
   * appointments would have vanished from a solo owner's day the moment she
   * retired somebody. Shading is a hint; membership is not.
   */
  shades: Resource | null;
  /** A lane's own name, or `null` when the column is a day. */
  heading: string | null;
}

/**
 * **The body moves the booking; the bottom edge changes its length.**
 *
 * Two gestures on one block, and two different verbs on the server — a move
 * keeps the span it was booked at (A1, enforced in three places) and a retime
 * keeps the start. Sharing the drag machinery between them is what keeps one
 * clash check, one ghost and one confirm sheet; what is *not* shared is the
 * intent, because a task list in which "moved" and "made it 90 minutes" were
 * the same row would be a task list that cannot say what went wrong.
 */
type DragMode = 'move' | 'length';

interface DragState {
  block: PlacedBlock;
  pointerId: number;
  grabOffsetMin: number;
  day: string;
  /** The new start minute under `move`; the new **length** under `length`. */
  minutes: number;
  /**
   * Under `length`, on a service sold at several lengths: **which of her
   * lengths the edge is on**. Absent everywhere else — a move, a service sold
   * one way, a keyboard resize not yet stepped.
   */
  stop?: LengthStop | undefined;
  resourceId: string;
  via: 'pointer' | 'keyboard';
  mode: DragMode;
  /** A touch is not a drag until it has been held. */
  armed: boolean;
  startX: number;
  startY: number;
}

interface ConfirmState {
  block: PlacedBlock;
  candidate: CandidateMove;
  /**
   * `move` · `propose` (a `requested` hour is offered, never moved — A5) ·
   * `length` ("this one ran long").
   */
  kind: 'move' | 'propose' | 'length';
  /** The option a `length` lands on, when it landed on one of hers. */
  stop?: LengthStop | undefined;
}

function confirmFor(state: DragState, candidate: CandidateMove): ConfirmState {
  return {
    block: state.block,
    candidate,
    ...(state.mode === 'length' && state.stop !== undefined ? { stop: state.stop } : {}),
    kind:
      state.mode === 'length'
        ? 'length'
        : state.block.appointment.status === 'requested'
          ? 'propose'
          : 'move',
  };
}

/** 5..480, the same bounds `ClientDurationMinutes` puts on the wire. */
function clampLength(minutes: number, policy: CalendarPolicy): number {
  return Math.min(480, Math.max(5, snapToGrid(minutes, policy.slotGridMin) || 5));
}

function Column({
  column,
  columns,
  bounds,
  marks,
  blocks,
  hours,
  blackouts,
  timezone,
  currency,
  locale,
  t,
  today,
  nowMinutes,
  drag,
  clash,
  canWrite,
  showHeading,
  register,
  onPickUp,
  onKey,
  onBook,
}: {
  column: ColumnSpec;
  columns: number;
  bounds: GridBounds;
  marks: number[];
  blocks: PlacedBlock[];
  hours: DayHours[];
  blackouts: Blackout[];
  timezone: string;
  currency: string;
  locale: UiLocale;
  t: T;
  today: string;
  nowMinutes: number;
  drag: DragState | null;
  clash: MirroredAppointment | undefined;
  /** Whether this seat may book, move or resize — `false` draws the grid and no doors. */
  canWrite: boolean;
  showHeading: boolean;
  register: (element: HTMLDivElement | null) => void;
  onPickUp: (
    event: ReactPointerEvent<HTMLElement>,
    block: PlacedBlock,
    column: ColumnSpec,
    mode?: DragMode,
  ) => void;
  onKey: (
    event: ReactKeyboardEvent<HTMLElement>,
    block: PlacedBlock,
    column: ColumnSpec,
    mode?: DragMode,
  ) => void;
  onBook: (minutes: number) => void;
}): ReactNode {
  const dayHours = hoursForDay(column.shades, hours, column.day);
  const open = openBands(dayHours, bounds);
  const closures = closureBands(
    blackouts,
    column.day,
    column.shades?.resource_id ?? null,
    timezone,
    bounds,
  );
  const isToday = column.day === today;
  const at = (minutes: number): string => `calc(var(--cal-ppm) * ${minutes - bounds.fromMin}px)`;
  const tall = (minutes: number): string => `calc(var(--cal-ppm) * ${minutes}px)`;

  /** The ghost only belongs in the column the drag is currently over. */
  const ghost =
    drag !== null &&
    drag.armed &&
    drag.day === column.day &&
    (column.resource === null || column.resource.resource_id === drag.resourceId)
      ? drag
      : null;

  return (
    <div
      className={`cal__column${isToday ? ' is-today' : ''}`}
      style={{ flex: `1 1 ${100 / columns}%` }}
    >
      {showHeading ? (
        <div className="cal__column-head">
          {column.heading ?? formatColumnDay(column.day, timezone, locale)}
        </div>
      ) : null}

      <div
        className="cal__body"
        ref={register}
        style={{ height: tall(bounds.toMin - bounds.fromMin) }}
      >
        {/*
         * **Shut by default, open where the hours say so.** The body's own
         * background is the closed shade and the open windows are painted on
         * top, which is the honest way round: a day with no hours document at
         * all reads as closed rather than as open all night.
         */}
        {open.map((band) => (
          <div
            key={`open-${band.fromMin}`}
            className="cal__open"
            style={{ top: at(band.fromMin), height: tall(band.toMin - band.fromMin) }}
          />
        ))}

        {closures.map((band) => (
          <div
            key={`shut-${band.fromMin}`}
            className="cal__closure"
            title={t('calendar.closed')}
            style={{ top: at(band.fromMin), height: tall(band.toMin - band.fromMin) }}
          />
        ))}

        {marks.map((minute) => (
          <div
            key={`line-${minute}`}
            className="cal__line"
            aria-hidden="true"
            style={{ top: at(minute) }}
          />
        ))}

        {/*
         * **Tap an empty stretch to book it.** One surface rather than a button
         * per step, because the useful precision here is a finger's and the
         * grid is continuous; the click's own Y decides the minute. Activated
         * from the keyboard there is no Y, so it prefills the start of the
         * day's first open window — an honest answer rather than midnight.
         */}
        {canWrite ? (
          <button
            type="button"
            className="cal__open-surface"
            onClick={(event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              const keyboard = event.detail === 0 || rect.height === 0;
              const ratio = keyboard ? 0 : (event.clientY - rect.top) / rect.height;
              const raw = bounds.fromMin + ratio * (bounds.toMin - bounds.fromMin);
              onBook(keyboard ? (open[0]?.fromMin ?? bounds.fromMin) : raw);
            }}
          >
            <span className="visually-hidden">
              {column.resource === null
                ? t('calendar.bookOn', { day: formatColumnDay(column.day, timezone, locale) })
                : t('calendar.bookAt', {
                    resource: column.resource.name,
                    day: formatColumnDay(column.day, timezone, locale),
                  })}
            </span>
          </button>
        ) : null}

        {blocks.map((block) => (
          <Block
            key={block.appointment.appointment_id}
            block={block}
            column={column}
            at={at}
            tall={tall}
            timezone={timezone}
            locale={locale}
            t={t}
            dragging={drag?.block.appointment.appointment_id === block.appointment.appointment_id}
            canWrite={canWrite}
            onPickUp={onPickUp}
            onKey={onKey}
          />
        ))}

        {/*
         * The ghost. A move floats it to the new hour; a resize pins its top to
         * where the guest was told to arrive and grows the bottom, because that
         * is the promise each gesture is and is not changing.
         */}
        {ghost === null ? null : (
          <div
            className={`cal__ghost${clash === undefined ? '' : ' cal__ghost--clash'}`}
            style={
              ghost.mode === 'length'
                ? { top: at(ghost.block.topMin), height: tall(ghost.minutes) }
                : { top: at(ghost.minutes), height: tall(ghost.block.heightMin) }
            }
          >
            <strong>
              {ghost.mode === 'length'
                ? ghost.stop === undefined
                  ? t('calendar.ghostLength', { minutes: ghost.minutes })
                  : // The snap target, said with what it costs on her menu.
                    t('calendar.ghostLengthPriced', {
                      minutes: ghost.stop.minutes,
                      price: formatMoney(ghost.stop.priceCents, currency, locale),
                    })
                : formatClock(isoAt(ghost.day, ghost.minutes, timezone), timezone, locale)}
            </strong>
            {clash === undefined ? null : (
              <small>
                {t('calendar.ghostClash', {
                  name: clash.client.display_name,
                  clock: formatClock(clash.slot.start, timezone, locale),
                })}
              </small>
            )}
          </div>
        )}

        {isToday && nowMinutes >= bounds.fromMin && nowMinutes <= bounds.toMin ? (
          <div className="cal__now" style={{ top: at(nowMinutes) }} aria-hidden="true" />
        ) : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* One block                                                           */
/* ------------------------------------------------------------------ */

function Block({
  block,
  column,
  at,
  tall,
  timezone,
  locale,
  t,
  dragging,
  canWrite,
  onPickUp,
  onKey,
}: {
  block: PlacedBlock;
  column: ColumnSpec;
  at: (minutes: number) => string;
  tall: (minutes: number) => string;
  timezone: string;
  locale: UiLocale;
  t: T;
  dragging: boolean;
  canWrite: boolean;
  onPickUp: (
    event: ReactPointerEvent<HTMLElement>,
    block: PlacedBlock,
    column: ColumnSpec,
    mode?: DragMode,
  ) => void;
  onKey: (
    event: ReactKeyboardEvent<HTMLElement>,
    block: PlacedBlock,
    column: ColumnSpec,
    mode?: DragMode,
  ) => void;
}): ReactNode {
  const appointment = block.appointment;
  const width = 100 / block.columns;
  const style: CSSProperties = {
    top: at(block.topMin),
    height: tall(block.heightMin),
    left: `${block.column * width}%`,
    width: `${width}%`,
  };
  const requested = appointment.status === 'requested';
  const clock = formatClock(appointment.slot.start, timezone, locale);
  /**
   * **"Custom Deluxe Facial · 60 min"** — the length it is booked at, on the
   * block itself. A facial shortened on the calendar but still sold as the 120
   * was invisible until the till; with the length printed beside the name, a
   * block drawn an hour tall that says anything else is a mismatch in plain
   * sight.
   */
  const bookedAs = `${appointment.service_name} · ${t('calendar.ghostLength', { minutes: block.heightMin })}`;
  /**
   * **Drawn in a lane it occupies but does not lead** (0056): the room's copy
   * of a massage whose primary lane is the therapist. It shows the room is
   * taken — which is the whole reason the row is on this column — and it is
   * deliberately not a second handle on the same appointment: dragging the
   * room's copy into another room's column would have read as "change the
   * primary lane to that room", and the person's block is the one that moves.
   */
  const companion =
    column.resource !== null && column.resource.resource_id !== appointment.slot.resource_id;

  /**
   * **For a seat that cannot write, every block is drawn the companion's way**:
   * a labelled note, no `tabIndex`, no handle, no handlers. The same words a
   * screen reader gets on the button — who, what, when, how long — without the
   * "move with the arrow keys" description that would promise a verb this seat
   * does not have. It is one branch rather than a `disabled` button because a
   * disabled button is still a door, announced as one.
   */
  if (!canWrite && !companion) {
    return (
      <>
        {block.tailMin > 0 ? (
          <div
            className="cal__tail"
            aria-hidden="true"
            title={t('calendar.gapAfter', { minutes: block.tailMin })}
            style={{
              top: at(block.endMin),
              height: tall(block.tailMin),
              left: style.left,
              width: style.width,
            }}
          />
        ) : null}
        <div
          role="note"
          aria-label={t('calendar.blockLabel', {
            name: appointment.client.display_name,
            service: appointment.service_name,
            clock,
            minutes: block.heightMin,
          })}
          className={[
            'cal__block',
            `cal__block--${appointment.source}`,
            `cal__block--${appointment.status}`,
            requested ? 'cal__block--requested' : '',
            block.pending ? 'cal__block--pending' : '',
          ]
            .filter((c) => c !== '')
            .join(' ')}
          style={style}
        >
          <strong>{clock}</strong>
          <span>{appointment.client.display_name}</span>
          <small>
            {bookedAs}
            {block.pending
              ? ` · ${t('today.waitingToSend')}`
              : requested
                ? ` · ${t('requests.tentative')}`
                : ` · ${sourceWord(appointment.source, t)}`}
          </small>
        </div>
      </>
    );
  }

  if (companion) {
    return (
      <div
        role="note"
        aria-label={t('calendar.companionLabel', {
          name: appointment.client.display_name,
          service: appointment.service_name,
          clock,
        })}
        className={[
          'cal__block',
          'cal__block--companion',
          `cal__block--${appointment.status}`,
        ].join(' ')}
        style={style}
      >
        <strong>{clock}</strong>
        <span>{appointment.client.display_name}</span>
        <small>{bookedAs}</small>
      </div>
    );
  }

  return (
    <>
      {/*
       * **The house gap, drawn as a lighter tail after the booking.**
       *
       * It is the room the appointment really takes off the resource — the span
       * the exclusion constraint holds — and until it was drawn the owner could
       * see a free half hour on this screen that the server would refuse. It is
       * deliberately not part of the block: it is not time anybody is in the
       * room, it is time nobody else may have, and the two read differently.
       */}
      {block.tailMin > 0 ? (
        <div
          className="cal__tail"
          aria-hidden="true"
          title={t('calendar.gapAfter', { minutes: block.tailMin })}
          style={{
            top: at(block.endMin),
            height: tall(block.tailMin),
            left: style.left,
            width: style.width,
          }}
        />
      ) : null}

      <div
        role="button"
        tabIndex={0}
        aria-describedby="cal-move-hint"
        aria-label={t('calendar.blockLabel', {
          name: appointment.client.display_name,
          service: appointment.service_name,
          clock,
          minutes: block.heightMin,
        })}
        className={[
          'cal__block',
          `cal__block--${appointment.source}`,
          `cal__block--${appointment.status}`,
          requested ? 'cal__block--requested' : '',
          block.pending ? 'cal__block--pending' : '',
          dragging ? 'is-dragging' : '',
        ]
          .filter((c) => c !== '')
          .join(' ')}
        style={style}
        onPointerDown={(event) => onPickUp(event, block, column)}
        onKeyDown={(event) => onKey(event, block, column)}
      >
        <strong>{clock}</strong>
        <span>{appointment.client.display_name}</span>
        <small>
          {bookedAs}
          {block.pending
            ? ` · ${t('today.waitingToSend')}`
            : requested
              ? ` · ${t('requests.tentative')}`
              : ` · ${sourceWord(appointment.source, t)}`}
        </small>

        {/*
         * **The bottom edge: "this one ran long"** (S1).
         *
         * Not drawn on a request, and that is a rule rather than a gap: a
         * `requested` hour has not been agreed to, and quietly making somebody
         * else's ask ninety minutes long is not an answer to it. A request is
         * confirmed, declined or offered another time, and nothing else.
         *
         * It is its own focusable control with its own arrow keys, so the
         * keyboard has both gestures rather than one — a screen reader reads
         * "make this longer or shorter" and gets a length, not a time.
         */}
        {requested ? null : (
          <div
            role="button"
            tabIndex={0}
            className="cal__handle"
            aria-label={t('calendar.lengthLabel', {
              name: appointment.client.display_name,
              minutes: block.heightMin,
            })}
            onPointerDown={(event) => onPickUp(event, block, column, 'length')}
            onKeyDown={(event) => {
              // The handle sits inside the block, and React bubbles: without
              // this, every arrow key would be read twice — once as a length
              // and once as a time — and the second reading would win.
              event.stopPropagation();
              onKey(event, block, column, 'length');
            }}
          />
        )}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* The confirm sheet                                                   */
/* ------------------------------------------------------------------ */

/**
 * **What a move actually does, said plainly, including what it does not do.**
 *
 * The obvious sentence to put here was *"She will be texted"*, and it would have
 * been false. A reschedule emits `booking.updated`, which reaches the **owner**
 * (E2's alert) and the partner's webhook; the guest is told nothing. Her
 * reminder, if this tenant has them on, is re-planned against the new time by
 * `planReminders` — which is a reminder arriving at the right hour, not a
 * message saying the hour changed. So the sheet says the guest is not told,
 * which is exactly the prompt the owner of Love Spa needs: she texts her clients
 * herself, warmly, in batches (`docs/lovespa-discovery.md` §6), and the useful
 * thing this screen can do is remind her that this one is hers to send.
 *
 * A **request** is the exception and the copy changes completely, because the
 * verb does: A5's propose *does* text the guest, and the hour stays hers to
 * accept or not.
 */
/** One row per verb, so a fourth is a row rather than a third ternary. */
const TITLES = {
  move: 'calendar.moveTitle',
  propose: 'calendar.proposeTitle',
  length: 'calendar.lengthTitle',
} as const;

const ACTIONS = {
  move: 'calendar.move',
  propose: 'calendar.propose',
  length: 'calendar.lengthApply',
} as const;

function MoveSheet({
  confirm,
  timezone,
  currency,
  rows,
  services,
  policy,
  onCancel,
  onMove,
  onPropose,
  onLength,
}: {
  confirm: ConfirmState;
  timezone: string;
  currency: string;
  rows: readonly MirroredAppointment[];
  services: readonly Service[];
  policy: CalendarPolicy;
  onCancel: () => void;
  onMove: () => Promise<void>;
  onPropose: () => Promise<void>;
  onLength: () => Promise<void>;
}): ReactNode {
  const t = useT();
  const locale = useLocale();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const appointment = confirm.block.appointment;
  const clash = clashFor(confirm.candidate, rows, services, policy);
  const when = `${formatColumnDay(
    wallClockOf(confirm.candidate.startIso, timezone).day,
    timezone,
    locale,
  )} ${formatClock(confirm.candidate.startIso, timezone, locale)}`;

  const run = async (action: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sheet" role="dialog" aria-label={t('calendar.moveDialog')}>
      <div className="sheet__panel">
        <h2>{t(TITLES[confirm.kind])}</h2>
        <p>
          {confirm.kind === 'propose'
            ? t('calendar.proposeQuestion', {
                name: appointment.client.display_name,
                service: appointment.service_name,
                when,
              })
            : confirm.kind === 'length'
              ? t('calendar.lengthQuestion', {
                  name: appointment.client.display_name,
                  service: appointment.service_name,
                  minutes: confirm.candidate.durationMin,
                })
              : t('calendar.moveQuestion', {
                  name: appointment.client.display_name,
                  service: appointment.service_name,
                  when,
                })}
        </p>
        {/* The length never changes on a move (A1) — say so, because the
            question "will she still have her hour" is the one the owner asks. */}
        <p className="muted">
          {confirm.kind === 'propose'
            ? t('calendar.proposeTexts')
            : confirm.kind === 'length'
              ? // One of her lengths is a different thing sold, at its own
                // menu price; any other length is the same thing, longer.
                confirm.stop !== undefined && confirm.stop.optionId !== appointment.option_id
                ? t('calendar.lengthOptionNote', {
                    was: confirm.block.heightMin,
                    option: confirm.stop.name,
                    price: formatMoney(confirm.stop.priceCents, currency, locale),
                  })
                : t('calendar.lengthNote', { was: confirm.block.heightMin })
              : t('calendar.moveNotTexted', { minutes: confirm.candidate.durationMin })}
        </p>

        {clash === undefined ? null : (
          <p className="warning">
            {t('calendar.moveClash', {
              name: clash.client.display_name,
              clock: formatClock(clash.slot.start, timezone, locale),
            })}
          </p>
        )}

        {error === null ? null : <p className="error">{error}</p>}

        <div className="sheet__actions">
          <button type="button" className="button button--ghost" onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={busy}
            onClick={() =>
              void run(
                confirm.kind === 'propose'
                  ? onPropose
                  : confirm.kind === 'length'
                    ? onLength
                    : onMove,
              )
            }
          >
            {t(ACTIONS[confirm.kind])}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * **Moving a request is proposing, not rescheduling** (A5).
 *
 * A `requested` appointment holds its hour and has not been agreed to. Dragging
 * it into a confirmed booking would be the owner answering a question the guest
 * asked by pretending she never asked it — so the drag lands in the flow the
 * Requests panel already owns, `POST /v1/requests/{id}/propose`, with the time
 * she dropped it on filled in. The request stays a request, moves to the new
 * hour, and the guest is texted to accept.
 *
 * The request's id **is** the appointment's id (`repos/requests.ts` mints one
 * `ap_` for both), which is what lets a block on the calendar name a row in that
 * queue without a second read.
 *
 * Online, deliberately, exactly as `Requests.tsx` is: the answer sends a text
 * and the hold may have expired, and neither is something a device with no
 * network can usefully decide.
 */
async function proposeAnotherTime(
  runtime: ReturnType<typeof useRuntime>,
  confirm: ConfirmState,
): Promise<void> {
  await runtime.api.request({
    method: 'POST',
    path: `/requests/${encodeURIComponent(confirm.block.appointment.appointment_id)}/propose`,
    body: { start: confirm.candidate.startIso, client_intent_id: intentId() },
    schema: StaffRequest,
  });
}

function intentId(): string {
  const random = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (typeof random?.randomUUID === 'function') return random.randomUUID();
  return `i-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/* ------------------------------------------------------------------ */
/* Small helpers                                                       */
/* ------------------------------------------------------------------ */

/**
 * **A resource's own hours win over the house's.**
 *
 * `Resource.hours` has been on the wire since the engine could take a resource
 * override, and the Team screen is what will start filling it in. Reading it
 * here means a person's Tuesday off appears as closed shading in her lane the
 * moment somebody sets it, with no edit to this file — which is the whole point
 * of reading lanes out of the mirror rather than out of a sibling's module.
 */
function hoursForDay(
  resource: Resource | null,
  tenantHours: readonly DayHours[],
  day: string,
): DayHours | undefined {
  const weekday = weekdayIndex(day);
  const own = resource?.hours ?? null;
  if (own !== null) return own.find((h) => h.weekday === weekday);
  return tenantHours.find((h) => h.weekday === weekday);
}

/** A day and a minute in the tenant's zone, as an instant. One implementation. */
function isoAt(day: string, minutes: number, timezone: string): string {
  return wallClockToIso(day, Math.min(1439, Math.max(0, Math.round(minutes))), timezone);
}

function formatColumnDay(day: string, timezone: string, locale: UiLocale): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(new Date(`${day}T12:00:00Z`));
}

function headingFor(days: readonly string[], timezone: string, locale: UiLocale): string {
  const first = days[0] ?? '';
  const last = days[days.length - 1] ?? first;
  const long = new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
  if (days.length <= 1) return long.format(new Date(`${first}T12:00:00Z`));
  const short = new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    month: 'short',
    day: 'numeric',
  });
  return `${short.format(new Date(`${first}T12:00:00Z`))} – ${short.format(new Date(`${last}T12:00:00Z`))}`;
}

/**
 * Phone or not, from the viewport rather than from the user agent.
 *
 * 720px is where seven columns stop being wider than the names in them. It is a
 * `useState` + a resize listener rather than `matchMedia` because the app is
 * rendered in jsdom by its own suite, where `matchMedia` does not exist — a
 * layout decision that cannot be tested is a layout decision that will regress.
 */
function useNarrowViewport(): boolean {
  const read = (): boolean => (globalThis.innerWidth ?? 1024) < 720;
  const [narrow, setNarrow] = useState(read);
  useEffect(() => {
    const onResize = (): void => setNarrow(read());
    onResize();
    globalThis.addEventListener?.('resize', onResize);
    return () => globalThis.removeEventListener?.('resize', onResize);
  }, []);
  return narrow;
}
